/**
 * Détecte les nouvelles versions du portail étudiant PWA.
 * Fetch /index.html toutes les 5 min et compare le hash du bundle main.<hash>.js
 * avec celui chargé actuellement. Si différent → affiche un banner en bas de page
 * "Nouvelle version disponible · Recharger".
 *
 * Nécessaire parce que les PWA installées (iOS notamment) mettent longtemps à
 * détecter un nouveau bundle même quand le nom du fichier a changé.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { RefreshCw, X } from 'lucide-react';

const CHECK_INTERVAL_MS = 5 * 60 * 1000; // 5 min
const CURRENT_BUNDLE_HASH_KEY = 'portail_bundle_hash';

// Récupère le hash du bundle main.js chargé actuellement (à partir des <script> du DOM)
function getLoadedBundleHash(): string | null {
  const scripts = document.querySelectorAll('script[src]');
  for (const s of Array.from(scripts)) {
    const src = (s as HTMLScriptElement).src || '';
    const m = src.match(/main\.([a-f0-9]+)\.js/);
    if (m) return m[1];
  }
  return null;
}

async function fetchRemoteBundleHash(): Promise<string | null> {
  try {
    // Cache-busting query : force nginx à renvoyer l'index.html courant
    const r = await fetch(`/index.html?t=${Date.now()}`, {
      cache: 'no-store',
      credentials: 'omit',
    });
    if (!r.ok) return null;
    const html = await r.text();
    const m = html.match(/main\.([a-f0-9]+)\.js/);
    return m ? m[1] : null;
  } catch {
    return null;
  }
}

export const UpdateAvailableBanner: React.FC = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const checkForUpdate = useCallback(async () => {
    const loaded = getLoadedBundleHash();
    if (!loaded) return;
    const remote = await fetchRemoteBundleHash();
    if (!remote) return;
    // Mémorise le hash chargé (pour détecter les changements)
    localStorage.setItem(CURRENT_BUNDLE_HASH_KEY, loaded);
    if (remote !== loaded) {
      setUpdateAvailable(true);
    }
  }, []);

  useEffect(() => {
    // Check initial (au bout de 20s pour éviter le check immédiat après un reload)
    const initial = setTimeout(checkForUpdate, 20_000);
    // Check périodique
    const iv = setInterval(checkForUpdate, CHECK_INTERVAL_MS);
    // Check quand l'app redevient visible (retour d'onglet / d'arrière-plan)
    const onVisibility = () => {
      if (document.visibilityState === 'visible') checkForUpdate();
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      clearTimeout(initial);
      clearInterval(iv);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [checkForUpdate]);

  const reload = () => {
    // Purge tous les caches SW (Cache Storage) avant le reload — nécessaire pour
    // iOS PWA installées qui gardent parfois l'ancien index.html.
    try {
      if ('caches' in window) {
        (caches as any).keys().then((keys: string[]) =>
          Promise.all(keys.map(k => (caches as any).delete(k))),
        );
      }
    } catch { /* ignore */ }
    // location.reload(true) déprécié → force fetch en changeant le hash
    window.location.reload();
  };

  if (!updateAvailable || dismissed) return null;

  return (
    <div className="fixed bottom-3 left-3 right-3 sm:left-auto sm:right-3 sm:w-96 z-[80]">
      <div className="bg-blue-600 text-white rounded-xl shadow-2xl border border-blue-500 p-3 flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
          <RefreshCw size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold">Nouvelle version disponible</div>
          <div className="text-[11px] text-blue-100 mt-0.5">
            Rechargez pour bénéficier des dernières améliorations.
          </div>
        </div>
        <button
          onClick={reload}
          className="px-3 py-1.5 bg-white text-blue-700 text-xs font-bold rounded-lg hover:bg-blue-50"
        >
          Recharger
        </button>
        <button
          onClick={() => setDismissed(true)}
          className="p-1 text-blue-100 hover:text-white"
          title="Plus tard"
        >
          <X size={14} />
        </button>
      </div>
    </div>
  );
};

export default UpdateAvailableBanner;
